import { ComponentProps, MouseEvent, useMemo } from "react";
import { Link, LinkProps } from "react-router-dom";

import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import * as Text from "typography/buttons";

import { Spinner } from "minimum-ui/core/Spinner/Spinner";

export type Variant =
  | "primary"
  | "secondary"
  | "error"
  | "error-secondary"
  | "outlined-primary"
  | "outlined-error"
  | "text-primary"
  | "text-primary-underline"
  | "text-only-primary-underline"
  | "text-error"
  | "text-white";

type Size = "large" | "medium" | "small" | "inherit";

export interface ButtonProps {
  /**
   * Pass in a string to behave as an HTML a tag, otherwise button is used
   */
  onClick?: string | ((e: MouseEvent<HTMLElement>) => void);
  /**
   * Affects padding and text size
   */
  size?: Size;
  /**
   * Controls color and border styles
   */
  variant?: Variant;
  /**
   * Is this button acting as a submit button for a form element?
   */
  submit?: boolean;
  /**
   * Passthrough for restyling
   */
  className?: string;
  disabled?: boolean;
  /**
   * Show loading spinner?
   */
  loading?: boolean;
  download?: string;
  /**
   * Open link in a new tab (only for string onClick values)
   */
  newTab?: boolean;
  /**
   * Optionally pass a state object to Link
   */
  state?: Record<string, any>;
  /**
   * Optionally pass a data test id for testing library selectors
   */
  dataTestId?: string;
}

type HTMLButtonProps = Omit<ComponentProps<"button">, "ref">;
type HTMLAnchorProps = Omit<ComponentProps<"a">, "ref">;
type ComponentAndProps =
  | [typeof Link, LinkProps]
  | ["button", HTMLButtonProps]
  | ["a", HTMLAnchorProps];

const Button: React.FC<ButtonProps> = ({
  onClick,
  size = "medium",
  variant = "primary",
  className,
  submit = false,
  children,
  disabled,
  loading,
  download,
  newTab = false,
  state,
  dataTestId = "",
}) => {
  const Text = TextStyles[size ?? "medium"];
  const [BaseComponent, baseProps] = useMemo<ComponentAndProps>(() => {
    if (typeof onClick === "string") {
      if (download || /^blob:/.test(onClick) || newTab) {
        return [
          "a",
          {
            download,
            href: onClick,
            onClick: function (e: MouseEvent) {
              if (disabled || loading) {
                e.preventDefault();
              }
            },
            ...(newTab ? { target: "_blank" } : {}),
          },
        ];
      }

      return [
        Link,
        {
          to: onClick,
          state: state,
          onClick: function (e: MouseEvent) {
            if (disabled || loading) {
              e.preventDefault();
            }
          },
        },
      ];
    } else {
      return [
        "button",
        {
          type: submit ? "submit" : "button",
          disabled: disabled || loading,
          onClick,
        },
      ];
    }
  }, [submit, onClick, disabled, loading, download, newTab, state]);

  return (
    <ButtonWrapper
      as={BaseComponent}
      {...baseProps}
      className={className}
      data-testid={dataTestId}
      $variant={variant}
      $size={size}
      $disabled={disabled}
      $loading={loading}
    >
      <Label as={Text}>{children}</Label>
      {loading && (
        <Loader>
          <Spinner
            size={LoaderSize[size ?? "medium"]}
            color={LoaderColor[variant]}
          />
        </Loader>
      )}
    </ButtonWrapper>
  );
};

const ButtonWrapper = styled.button<{
  $variant: Variant;
  $size: Size;
  $disabled?: boolean;
  $loading?: boolean;
}>`
  display: inline-flex;
  place-content: center;
  text-decoration: none;
  border-radius: 4px;
  border: var(--border, 0px) solid var(--color);
  padding: var(--padding, 0px);
  background: var(--background, transparent);
  color: var(--color);
  margin: 0;
  cursor: pointer;
  transition: background 0.3s ease, color 0.2s ease;
  position: relative;
  overflow: hidden;

  // Default colors
  --disabled-background: ${color.foreground.disabled};
  --disabled-color: ${color.basic.white};

  // Initial state
  --background: var(--initial-background);
  --color: var(--initial-color);

  &:hover {
    --background: var(--hover-background, var(--initial-background));
    --color: var(--hover-color, var(--initial-color));
  }

  &:focus-visible {
    --background: var(
      --focus-background,
      var(--hover-background, var(--initial-background))
    );
    --color: var(--focus-color, var(--hover-color, var(--initial-color)));
  }

  &:active {
    --background: var(--active-background, var(--initial-background));
    --color: var(--active-color, var(--initial-color));
  }

  ${(props) =>
    props.$loading &&
    css`
      cursor: wait;
      --background: var(
        --loading-background,
        var(--active-background, var(--initial-background))
      ) !important;
      --color: var(
        --loading-color,
        var(--active-color, var(--initial-color))
      ) !important;
    `}

  ${(props) =>
    props.$disabled &&
    css`
      cursor: not-allowed;
      --background: var(
        --disabled-background,
        var(--initial-background)
      ) !important;
      --color: var(--disabled-color, var(--initial-color)) !important;
    `}


  ${(props) => VariantStyles[props.$variant]}
  ${(props) => SizeStyles[props.$size]}
`;
const Label = styled.span`
  color: var(--color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap, 8px);
`;
const Loader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--background);
  display: grid;
  place-content: center;
`;

const TextStyles: Record<Size, (typeof Text)[keyof typeof Text]> = {
  large: Text.LargeM,
  medium: Text.SmallM,
  small: Text.SmallM,
  inherit: Text.Inherit,
};

const LoaderSize: Record<Size, number> = {
  large: 24,
  medium: 20,
  small: 16,
  inherit: 16,
};

const LoaderColor: Record<string, string> = {
  primary: color.basic.white,
  secondary: color.blue[300],
  error: color.red[300],
  "error-secondary": color.red[300],
  "outlined-primary": color.blue[300],
  "outlined-error": color.red[300],
  "text-primary": color.blue[300],
  "text-primary-underline": color.blue[300],
  "text-error": color.red[300],
  "text-white": color.basic.white,
};

const SizeStyles: Record<Size, Record<string, string>> = {
  large: {
    "--padding": "12px 16px",
    "--loader-padding": "8px 36px",
    "--gap": "12px",
  },
  medium: {
    "--padding": "8px 16px",
    "--loader-padding": "6px 26px",
    "--gap": "8px",
  },
  small: {
    "--padding": "4px 8px",
    "--loader-padding": "4px 20px",
    "--gap": "4px",
  },
  inherit: {},
};

const VariantStyles: Record<Variant, Record<string, string>> = {
  primary: {
    "--initial-background": color.blue[300],
    "--initial-color": color.basic.white,
    "--hover-background": color.blue[300],
    "--active-background": color.blue[400],
  },
  secondary: {
    "--initial-background": color.neutral[200],
    "--initial-color": color.blue[300],
    "--hover-background": color.neutral[200],
    "--active-background": color.neutral[300],
  },
  error: {
    "--initial-background": color.red[300],
    "--initial-color": color.basic.white,
    "--hover-background": color.red[400],
    "--active-background": color.red[500],
  },
  "error-secondary": {
    "--initial-background": color.red[200],
    "--initial-color": color.red[300],
    "--hover-background": color.red[200],
    "--hover-color": color.red[400],
    "--active-background": color.red[200],
    "--active-color": color.red[500],
    "--loading-background": color.red[200],
    "--loading-color": color.red[300],
  },
  "outlined-primary": {
    "--border": "1px",
    "--initial-background": "transparent",
    "--initial-color": color.blue[300],
    "--hover-background": color.blue[100],
    "--active-background": color.blue[200],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
  },
  "outlined-error": {
    "--border": "1px",
    "--initial-background": "transparent",
    "--initial-color": color.red[300],
    "--hover-background": color.red[200],
    "--hover-color": color.red[400],
    "--active-background": color.red[200],
    "--active-color": color.red[500],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
    "--loading-background": color.red[200],
    "--loading-color": color.red[300],
  },
  "text-primary": {
    "--initial-background": "transparent",
    "--initial-color": color.blue[300],
    "--hover-background": color.blue[100],
    "--active-background": color.blue[200],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
  },
  "text-primary-underline": {
    "--initial-background": "transparent",
    "--initial-color": color.blue[300],
    "--hover-background": color.blue[100],
    "--active-background": color.blue[200],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
    "text-decoration": "underline",
  },
  "text-only-primary-underline": {
    "--initial-background": "transparent",
    "--initial-color": color.blue[300],
    // "--hover-background": color.blue[100],
    // "--active-background": color.blue[200],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
    "text-decoration": "underline",
    margin: "0",
    padding: "0",
    "font-size": "inherit",
  },
  "text-error": {
    "--initial-background": "transparent",
    "--initial-color": color.red[300],
    "--hover-background": color.red[200],
    "--hover-color": color.red[400],
    "--active-background": color.red[200],
    "--active-color": color.red[500],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
    "--loading-background": color.red[200],
    "--loading-color": color.red[300],
  },
  "text-white": {
    "--initial-background": "transparent",
    "--initial-color": color.basic.white,
    "--hover-color": color.blue[300],
    "--active-color": color.blue[300],
    "--hover-background": color.blue[100],
    "--active-background": color.blue[200],
    "--disabled-color": color.neutral[400],
    "--disabled-background": "transparent",
    "--loading-background": "transparent",
    "--loading-color": color.basic.white,
  },
};

export default Button;
